import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

/**
 * Casing policy: badge labels are authored in Title Case ("In Review", "Plan Set") and rendered
 * verbatim. `casing="caps"` is only for deliberate eyebrow labels.
 */
const badgeVariants = cva(
  "inline-flex w-fit shrink-0 items-center justify-center gap-1 overflow-hidden rounded-md border font-semibold whitespace-nowrap transition-colors [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground border-transparent [a&]:hover:bg-primary/90",
        secondary:
          "bg-secondary text-secondary-foreground border-transparent [a&]:hover:bg-secondary/90",
        destructive:
          "bg-destructive text-white border-transparent [a&]:hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
        outline:
          "border-border text-foreground [a&]:hover:bg-accent [a&]:hover:text-accent-foreground",
        ghost: "border-transparent [a&]:hover:bg-accent [a&]:hover:text-accent-foreground",
        link: "border-transparent text-primary underline-offset-4 [a&]:hover:underline",
      },
      size: {
        sm: "h-5 px-1.5 text-[11px] leading-4 [&>svg]:size-3",
        default: "h-6 px-2 text-xs [&>svg]:size-3.5",
        lg: "h-7 gap-1.5 px-2.5 text-sm [&>svg]:size-4",
      },
      casing: {
        none: "",
        caps: "uppercase tracking-wider",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
      casing: "none",
    },
  }
)

type BadgeProps = React.ComponentProps<"span"> &
  VariantProps<typeof badgeVariants> & { asChild?: boolean }

function Badge({
  className,
  variant = "default",
  size = "default",
  casing = "none",
  asChild = false,
  ...props
}: BadgeProps) {
  const Comp = asChild ? Slot : "span"

  return (
    <Comp
      data-slot="badge"
      data-variant={variant}
      data-size={size}
      className={cn(badgeVariants({ variant, size, casing }), className)}
      {...props}
    />
  )
}

export { Badge, badgeVariants, type BadgeProps }
